<template>
    <article>

        <h2>TL;DR</h2>
        <p>
            The process of making a system observable can be quite time-consuming and costly.
            To address this, we built <a href="https://github.com/coroot/coroot" target="_blank">Coroot</a> &mdash;
            an open-source observability tool that enables users to get visibility into their systems in just a few minutes
            without any code changes.
            Coroot creates a digital model of each system and continuously audits every application using
            a set of predefined inspections based on industry best practices.
        </p>

        <h2>The problem</h2>

        <p>
            Observability is a critical aspect of any infrastructure, as it allows teams to identify and troubleshoot issues quickly.
            However, making a system observable is not without its costs.
            It's quite a time- and resource-consuming process since it requires adding instrumentation into every application.
        </p>

        <p>
            Let's see what the integration of an APM (Applications Performance Monitoring) tool into a system looks like in practice:
        </p>

        <ul>
            <li>
                Integrate the appropriate APM SDK into each service
            </li>
            <li>
                Enable request tracing in every ORM, database/queue/http/grpc client that is being used
            </li>
            <li>
                Test and re-deploy every service
            </li>
            <li>
                Figure out which dashboards can be helpful in troubleshooting and configure them
            </li>
            <li>
                Define alerting rules for your metrics and/or traces to be notified when something goes wrong
            </li>
        </ul>

        <p>
            My optimistic estimate is that it can take over 40 hours for an <i>experienced</i> engineer to instrument a system of 10 services.
            Keep in mind, however, that you will have to repeat most of these steps every time you run a new service.
            I think the term <i>"observability tax"</i> is particularly well-suited to describe
            the costs that companies have to incur in terms of time, resources, and effort if they want to maintain
            a high level of visibility into their infrastructures.
        </p>

        <p>
            Additionally, it can be challenging to manually instrument all parts of a system due to the presence of
            third-party and legacy services.
            This can result in "blind spots" where certain parts of the system are not observable.
        </p>

        <p>
            <i>However, there is good news!</i>
        </p>

        <h2>eBPF is revolutionizing security, networking, ... and &#127881;observability&#127881;</h2>

        <p>
            <a href="https://ebpf.io" target="_blank">eBPF</a> (extended Berkeley Packet Filter) is a game-changing
            technology that can eliminate the need to manually instrument application code.
            It allows users to attach custom programs to various parts of the Linux kernel, such as
            system calls, network functions, and tracepoints.
            Such eBPF programs can be used for a wide range of purposes, including networking, security, and observability.
        </p>

        <p>
            For example, we can create an eBPF program that traces all HTTP requests made by a container,
            and this will be effective for any app running on the host, whether it is <var>nginx</var>, <var>curl</var>,
            or a Java application.
        </p>

        <p>
            Given that the Linux kernel in most of its supported versions now offers at least minimal support for eBPF,
            we decided to create a real zero-instrumentation observability tool based on it.
        </p>

        <h2>Step #1: gathering telemetry data</h2>

        <p>
            <a href="https://github.com/coroot/coroot-node-agent" target="_blank">Coroot-node-agent</a> is a Prometheus
            exporter that is responsible for gathering metrics about all the containers that are running on a particular node.
            It uses eBPF for several purposes, such as:
        </p>

        <ul>
            <li>
                Container discovery: the agent traces the <var>task_newtask</var> and <var>sched_process_exit</var> tracepoints
                to discover the containers that are running on a node
            </li>
            <li>
                Tracing TCP connections: the <var>sys_connect</var> and <var>inet_sock_set_state</var> tracepoints
                allow the agent to discover TCP connections and LISTEN sockets of the container
            </li>
            <li>
                Tracing application layer protocol requests: the agent follows the
                <var>sys_write/writev/sendto</var> and <var>sys_read/readv/recvfrom</var> tracepoints to trace
                the requests that the containers make to other services using application layer protocols such as HTTP, Postgres, and Redis.
            </li>
        </ul>

        <div class="pa-5 my-6">
            <v-img eager max-width="650px" class="ma-auto" src="/static/img/blog/observability-tax/container_metrics.svg" />
        </div>

        <p>
            Alongside eBPF-based metrics, node-agent also gathers:
        </p>

        <ul>
            <li>
                <router-link target="_blank" :to="{name: 'blog', params: {name: 'mining-logs-from-unstructured-logs'}}">Log-based metrics</router-link>: the agent discovers and parses unstructured container logs and extracts repeated patterns from them.
            </li>
            <li>
                <router-link target="_blank" :to="{name: 'blog', params: {name: 'how-to-ping'}}">Network latency</router-link>: when the agent discovers an outbound TCP connection from a container, it starts to measure
                network RTT (Round-Trip Time) between this container and the destination IP address.
            </li>
            <li>
                <router-link target="_blank" :to="{name: 'blog', params: {name: 'linux-delay-accounting'}}">Delay accounting metrics</router-link>
            </li>
            <li>
                <router-link target="_blank" :to="{name: 'blog', params: {name: 'cloud-metadata'}}">Cloud instance metadata</router-link>
            </li>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'metric-exporters', group: 'node-agent', item: 'metrics'}, hash: 'jvm'}">JVM metrics</router-link>
            </li>
        </ul>


        <h2>Step #2: getting a clear picture of a system</h2>

        <p>
            While it is important to collect the right metrics, it is equally important to have a way to analyze and interpret the data
            in order to gain insights.
        </p>

        <p>
            We decided to build a separate tool because the built-in capabilities of <a href="https://github.com/grafana/grafana" target="_blank">Grafana</a>,
            a popular open-source data visualization platform, were not sufficient for our needs.
            I'll explain this by showing you <a href="https://github.com/coroot/coroot" target="_blank">Coroot</a> in action,
            so you can evaluate whether or not it could have been implemented with Grafana.
        </p>

        <p>
            I would like to highlight that only the following three commands were executed to integrate Coroot into the <a href="https://community-demo.coroot.com/" target="_blank">demo project</a>,
            and no further configuration was required:
        </p>

        <pre v-hl.sh>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update
helm install --namespace coroot --create-namespace coroot coroot/coroot
        </pre>

        <p>... 2 minutes later</p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/observability-tax/service_map.png" />
        </v-card>

        <p>
            In the screenshot above, you can see a service map of the system.
            It shows all applications, databases, and how they communicate with each other.
            Moreover, you can quickly assess the status of each application and the status of their key subsystems.
        </p>

        <p>
            Coroot comes with a set of predefined inspections which can identify most common issues within a system.
            These ready-to-use inspections need no additional configuration but can be easily adjusted, if necessary, for any application or an entire project.
        </p>

        <ul>
            <li>
                <b>SLOs</b> (Service Level Objectives): tracks the error rate and latency of every application and
                notifies you of any significant violation of the defined objectives
            </li>
            <li>
                <b>Instances</b>: detects when one or more instances of an app are unavailable or have been restarted
            </li>
            <li>
                <b>CPU</b>: identifies when an app is experiencing a lack of CPU time due to throttling or high CPU usage on the related nodes </li>
            <li>
                <b>Memory</b>: detects situations where an app's containers have been terminated by the OOM (Out Of Memory) killer
            </li>
            <li>
                <b>Storage</b>: identifies when the performance of a storage volume is affecting the application
            </li>
            <li>
                <b>Network</b>: detects network issues, such as increased latency or connectivity loss
            </li>
            <li>
                <b>Logs</b>: audits log-based metrics and highlights the errors
            </li>
        </ul>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/observability-tax/order.png" />
        </v-card>

        <p>
            To effectively troubleshoot an issue with a particular service,
            it is essential to have all relevant information about it in one place.

            Coroot simplifies this process by providing automatically generated dashboards for each application,
            allowing you to focus on any given app.
        </p>

        <p>
            As you can see above, each dashboard has a status, so you can quickly check if there are any issues
            without having to open them.
        </p>

        <h2>Step #3: rethinking alerting</h2>

        <p>
            Conventional alerting systems often suffer from a lack of context, as each alerting rule may only consider
            a single aspect of the system without considering the system as a whole.
        </p>

        <p>
            There are two main types of alerts:
        </p>

        <ul>
            <li>
                Symptom-based alerts identify issues that affect users of a service, such as when an application fails to meet its Service Level Objectives (SLOs)
            </li>
            <li>
                Cause-based (auxiliary) alerts help in root cause analysis and detect issues, such as network connectivity issues or high CPU usage on a node
            </li>
        </ul>

        <p>
            In practice, users often receive both types of alerts at the same time and must manually try to connect symptoms to possible causes.
            This can be particularly difficult when managing a complex system.
            Many services attempt to identify correlations between alerts,
            but most of them can only group alerts that occur around the same time without understanding the nature of these events.
        </p>

        <p>
            We decided to approach the problem from a different perspective.
        </p>

        <p>
            Coroot builds a model of each system, including all applications, their instances, related nodes,
            and links between services.
            With this model, Coroot is able to perform all inspections in the single context of a specific application.
            Therefore, if an application is not meeting its service level objectives,
            Coroot will send a single alert that includes the results of all relevant inspections.
        </p>

        <v-card max-width="800px" class="pa-5 my-6">
            <v-img eager class="ma-auto" src="/static/img/blog/observability-tax/slack.png" />
        </v-card>

        <p>
            This approach also eliminates false positives, as no alerts are sent when all SLOs are met.
        </p>

        <h2>Conclusion</h2>

        <p>
            We built Coroot because we believe it is unreasonable that it requires such a significant amount of effort
            to make systems observable.
            Our goal is to enable engineers to obtain the visibility they need to understand and troubleshoot their systems
            without the need to become experts in this area.
        </p>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>

    </article>
</template>

<script>

export default {
    components: {},
}
</script>

<style scoped>
</style>
